import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux'

import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import { PscPhrase } from '../components/psc/psc-phrase';
import Status from '../components/psc/lib/Status'
import NoAccess from '../components/psc/lib/NoAccess'
import SingleSelect from '../components/psc/lib/SingleSelect'
import TextArea from '../components/psc/lib/TextArea'
import PickDate from '../components/psc/lib/PickDate'
import Loading from '../components/psc/lib/Loading'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useProjectContents from '../hooks/useProjectContents';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

import Stages from '../views/Report/Stages';
import Contents from '../views/Report/Contents';

const Report = () => {
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const params = useParams();
    const isNew = params.objID === 'new';
    const projectID = Number(params.projectID);
    const objID = Number(params.objID);

    const { setBrowserTabTitle } = useBrowserTabTitle()

    const { currentUser } = useAuthentication();

    const {
        userHasProjectAccessLevel,
    } = useUserManagement()

    const {
        updateReport,
        deleteReport,
    } = useProjectContents()
    
    // Object fields
    const [id, setID] = useState(isNew ? NaN : objID)
    const [updatedAt, setUpdatedAt] = useState(null);
    const [reportSchemeID, setReportSchemeID] = useState(null);
    const [title, setTitle] = useState('');
    const [note, setNote] = useState('');
    const [createDate, setCreateDate] = useState(new Date());
    const [reportStatusID, setReportStatusID] = useState(1); // Default status id

    // Read-only
    const [isDirty, setIsDirty] = useState(false);
    const [hasAccess, setHasAccess] = useState(true);
    const [hasStages, setHasStages] = useState(false);
    const [reportStatuses, setReportStatuses] = useState([]);
    const [originalReportStatusID, setOriginalReportStatusID] = useState(1);
    const [viewDocxId, setViewDocxId] = useState(0);
    const [reportSchemes, setReportSchemes] = useState([]);
    const [reportDocuments, setReportDocuments] = useState([]);
    const [reportIncludedDocuments, setReportIncludedDocuments] = useState([]);
    const [reportCustomerDocuments, setReportCustomerDocuments] = useState([]);
    const [reportRecords, setReportRecords] = useState([]);
    const [reportModels, setReportModels] = useState([]);
    const [reportDocumentStatuses, setReportDocumentStatuses] = useState([]);
    const [reportIncludedDocumentStatuses, setReportIncludedDocumentStatuses] = useState([]);
    const [reportCustomerDocumentStatuses, setReportCustomerDocumentStatuses] = useState([]);
    const [reportRecordStatuses, setReportRecordStatuses] = useState([]);
    const [hasSmartTemplate, setHasSmartTemplate] = useState(false);
    const [reportStages, setReportStages] = useState([]);
    const [reportView, setReportView] = useState(null);

    // Form controls
    const [hasLoaded, setHasLoaded] = useState(isNew ? true: false)
    const [error, setError] = useState(false);
    const [submitActive, setSubmitActive] = useState(false);

    useEffect(() => {
        if (typeof project === 'undefined') {
            navigate('/login')
        }
    }, [project])

    useEffect(() => {
        fetch('/v0/report_statuses', { credentials: 'same-origin' })
            .then(response => response.json())
            .then(json => setReportStatuses(json.results))

        let schemeUrl = '/v0/report_schemes';
        if (isNew) {
            const query = new URLSearchParams(location.search);
            const has_stages = query.get('has_stages');
            if (has_stages && has_stages === '1') {
                schemeUrl += '?has_stages=1'
                setHasStages(true)
            } else {
                schemeUrl += '?has_stages=0'
                setHasStages(false)
            }
        }
        
        fetch(schemeUrl, { credentials: 'same-origin' })
            .then(response => response.json())
            .then(json => setReportSchemes(json.results))

        if (isNew) {
            setReportModels(getAllModels(project.project_systems))
            setIsDirty(true)
            return
        }
        
        fetchObject()
    }, [isNew, objID, location.search])

    useEffect(() => {
        if( isNew ) {
            setBrowserTabTitle('New Report')
            return
        }
        if( title ) {
            setBrowserTabTitle(title)
        }
    }, [isNew, title, setBrowserTabTitle])

    const getAllModels = (systems) => {
        let models = []
        systems.forEach((system) => {
            models = [ ...models, ...getAllModels(system.children) ]
            system.project_system_models.forEach((item) => {
                models.push({
                    project_system_model_id: item.project_system_model_id,
                    project_system_id: item.project_system_id,
                    model_id: item.model_id,
                    value: item.model_code,
                    label: item.model_code
                })
            })
        })
        return models
    }

    const fieldsDisabled = useMemo(() => {
        if (isNew) {
            return false
        }
        if (originalReportStatusID === 2) { // TODO HACK - This must be configurable at backend
            return true
        }
        if (!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')) {
            return true
        }
        return false
    }, [isNew, originalReportStatusID, currentUser, projectID])

    const fetchObject = () => {
        fetch('/v0/reports/' + objID, { credentials: 'same-origin' })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(json => {
                if(json.error) {
                    if(json.error === 'ENOACCESS') {
                        setHasAccess(false)
                        // Assuming you have access to dispatch
                        dispatch({ type: 'CLEAR_PROJECT' })
                    } else {
                        alert('Error: ' + json.error)
                    }
                    return
                }

                const result = json.result
                
                if (result.report_stages) {
                    result.report_stages = result.report_stages.map(s => ({
                        ...s,
                        timestamp: s.id
                    }))
                }
                
                setReportSchemeID(result.report_scheme_id)
                setTitle(result.title)
                setNote(result.note)
                setCreateDate(result.create_date)
                setReportStatusID(result.report_status_id)

                setHasStages(result.report_scheme.has_stages)
                setReportStages(result.report_stages || [])
                setOriginalReportStatusID(result.report_status_id)

                setReportDocuments(result.report_documents)
                setReportIncludedDocuments(result.report_included_documents)
                setReportCustomerDocuments(result.report_customer_documents)
                setReportRecords(result.report_records)
                             
                setReportModels(result.report_models)
                setReportDocumentStatuses(result.report_document_statuses)
                setReportIncludedDocumentStatuses(result.report_included_document_statuses)
                setReportCustomerDocumentStatuses(result.report_customer_document_statuses)
                setReportRecordStatuses(result.report_record_statuses)
                setHasSmartTemplate(result.has_smart_template)
                setReportView(result.report_view)
                setUpdatedAt(result.updated_at)

                setHasLoaded(true)
            })
            .catch(err => {
                console.log('Report fetch error', err)
                setHasAccess(false)
            })
    }

    const toBackend = () => {
        return {
            updated_at: updatedAt,
            project_id: projectID,
            report_scheme_id: reportSchemeID,
            title: title,
            note: note,
            create_date: createDate,
            report_status_id: reportStatusID,
            report_documents: reportDocuments,
            report_included_documents: reportIncludedDocuments,
            report_customer_documents: reportCustomerDocuments,
            report_records: reportRecords,
            report_models: reportModels,
            report_document_statuses: reportDocumentStatuses,
            report_included_document_statuses: reportIncludedDocumentStatuses,
            report_customer_document_statuses: reportCustomerDocumentStatuses,
            report_record_statuses: reportRecordStatuses,
            report_stages: reportStages
        }
    }

    const validateBeforeSubmit = () => {
        let errors = []

        if (!reportSchemeID) {
            errors.push('Report Scheme is required')
        }

        if (!title || /^\s+$/.test(title)) {
            errors.push('Title is required')
        }

        if (!createDate) {
            errors.push(PscPhrase('REPORT_LABEL_CREATE_DATE') + ' is required')
        }

        if (reportDocuments.find((elem) => elem.conflict === true) !== undefined) {
            errors.push('You have selected more than 1 version of a Document. Please select which version to use for this baseline.')
        }

        if (reportIncludedDocuments.find((elem) => elem.conflict === true) !== undefined) {
            errors.push('You have selected more than 1 version of an Included Document. Please select which version to use for this baseline.')
        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }
        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event, successCallback) => {
        if (event) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        let submit = {
            objType: 'reports',
            objID: id,
            data: toBackend(),
            successCallback: (json) => {
                const reportID = json.result.id
                setID(reportID) // Save it for subsequent saves on a new Report. This can happen as we're adding Baselines
                updateReport(json.result)
                setSubmitActive(false)
            }
        }

        return submit
    }

    if (!hasAccess) {
        return (<NoAccess />)
    }

    if (!hasLoaded) {
        return <Loading />
    }

    let newTitle = '<New Status Report>'
    let titleLabel = 'Status Report'
    if (hasStages) {
        newTitle = '<New Tracking Report>'
        titleLabel = 'Tracking Report'
    }
    
    let requiredIndicator = '';
    if (!fieldsDisabled) {
        requiredIndicator = (<label className="form-required-star">&nbsp;*</label>)
    }

    let deleteButton = []
    if (objID && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')) {
        const url = `/v0/reports/${objID}`;
        deleteButton.push(
            <DeleteButton
                url={url}
                setError={setError}
                callback={() => deleteReport(objID)}
            />
        )
    }

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>{titleLabel}: {title || newTitle}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(event) => event.preventDefault()}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('REPORT_LABEL_SCHEME')}
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8 report-scheme-selector">
                                        <SingleSelect
                                            value={reportSchemeID}
                                            onChange={(value) => setReportSchemeID(value.id)}
                                            options={reportSchemes}
                                            disabled={!isNew ? true : false}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_TITLE')}
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="title" value={title} onChange={(e) => setTitle(e.target.value)} required disabled={fieldsDisabled} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('REPORT_LABEL_NOTE')}</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="note" value={note} onChange={(e) => setNote(e.target.value)} disabled={fieldsDisabled} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('REPORT_LABEL_CREATE_DATE')}
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8" style={{zIndex: '0'}}>
                                        <PickDate
                                            value={createDate}
                                            onChange={(date) => setCreateDate(date)}
                                            disabled={fieldsDisabled} 
                                        />
                                    </div>
                                </div>

                                <Status
                                    value={reportStatusID}
                                    onChange={(value) => setReportStatusID(value) }
                                    statuses={reportStatuses}
                                    disabled={!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')} 
                                />

                                <div className="hr-line-dashed"></div>

                                <Contents
                                    reportID={id}
                                    reportSchemeID={reportSchemeID}
                                    originalReportStatusID={originalReportStatusID}
                                    hasStages={hasStages}
                                    disabled={!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')}
                                    reportView={reportView}

                                    reportDocumentStatuses={reportDocumentStatuses}
                                    reportIncludedDocumentStatuses={reportIncludedDocumentStatuses}
                                    reportCustomerDocumentStatuses={reportCustomerDocumentStatuses}
                                    reportRecordStatuses={reportRecordStatuses}

                                    setReportDocumentStatuses={setReportDocumentStatuses}
                                    setReportIncludedDocumentStatuses={setReportIncludedDocumentStatuses}
                                    setReportCustomerDocumentStatuses={setReportCustomerDocumentStatuses}
                                    setReportRecordStatuses={setReportRecordStatuses}

                                    reportDocuments={reportDocuments}
                                    reportIncludedDocuments={reportIncludedDocuments}
                                    reportCustomerDocuments={reportCustomerDocuments}
                                    reportRecords={reportRecords}
                                    
                                    setReportDocuments={setReportDocuments}
                                    setReportIncludedDocuments={setReportIncludedDocuments}
                                    setReportCustomerDocuments={setReportCustomerDocuments}

                                    setReportRecords={setReportRecords}
                                    reportModels={reportModels}
                                    setReportModels={setReportModels}
                                />
                                
                                <div className="hr-line-dashed"></div>

                                {hasStages &&
                                    <Stages
                                        has_stages={hasStages}
                                        report_title={title}
                                        project={project}
                                        has_smart_template={hasSmartTemplate}
                                        report_scheme={reportSchemes.find(s => s.id === reportSchemeID)}
                                        report_stages={reportStages}
                                        can_delete_stage={userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')}
                                        disabled={!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')}
                                        handleSubmit={handleSubmit}
                                    />
                                }
            
                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage error={error} />

                                    <div className="col-sm-8 col-sm-offset-2">
                                    <CancelButton/>

                                    <SaveButton
                                        buttonVariant='primary'
                                        visible={userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")}
                                        spinnerActive={submitActive}
                                        label='Save Changes'
                                        onClick={handleSubmit}
                                        onError={handleSubmitError}
                                    />

                                    {deleteButton}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
};

export default Report
